/**
 * Accept Invitation Page
 * Page for accepting an invitation and creating an account
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useValidateInvitation, useAcceptInvitation } from '@/hooks/useInvitations';
import { Input } from '@/components/common/Input';
import { Button } from '@/components/common/Button';
import { AuthLayout, AuthBrand } from './AuthLayout';

export const AcceptInvitationPage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const { data: invitation, isLoading: isValidating, error: validationError } = useValidateInvitation(token || null);
  const acceptMutation = useAcceptInvitation();

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    password: '',
    confirmPassword: '',
  });

  const [formErrors, setFormErrors] = useState<{
    firstName?: string;
    lastName?: string;
    displayName?: string;
    password?: string;
    confirmPassword?: string;
  }>({});

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    setSubmitError(null);
  };

  const validateForm = (): boolean => {
    const errors: typeof formErrors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'Името е задължително';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Фамилията е задължителна';
    }

    if (!formData.password) {
      errors.password = 'Паролата е задължителна';
    } else if (formData.password.length < 8) {
      errors.password = 'Паролата трябва да е поне 8 символа';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Паролите не съвпадат';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!validateForm() || !token) {
      return;
    }

    try {
      await acceptMutation.mutateAsync({
        token,
        input: {
          firstName: formData.firstName.trim(),
          lastName: formData.lastName.trim(),
          displayName: formData.displayName.trim() || undefined,
          password: formData.password,
        },
      });
      setSuccess(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Възникна грешка при създаването на акаунт');
    }
  };

  // Redirect to login after successful registration
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        navigate('/login');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [success, navigate]);

  // Loading state
  if (isValidating) {
    return (
      <AuthLayout>
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-400 mx-auto mb-4" />
          <p className="text-gray-400">Проверка на поканата...</p>
        </div>
      </AuthLayout>
    );
  }

  // Invalid token
  if (validationError || !invitation) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="text-red-400 text-5xl mb-4">!</div>
          <h1 className="text-2xl font-bold text-white mb-4">Невалидна покана</h1>
          <p className="text-gray-400 mb-6">
            {validationError instanceof Error
              ? validationError.message
              : 'Тази покана е невалидна, изтекла или вече е използвана.'}
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 rounded-xl transition-all shadow-lg shadow-primary-500/25"
          >
            Към страницата за вход
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Success state
  if (success) {
    return (
      <AuthLayout>
        <div className="text-center">
          <div className="text-green-400 text-5xl mb-4">&#10003;</div>
          <h1 className="text-2xl font-bold text-white mb-4">Акаунтът е създаден!</h1>
          <p className="text-gray-400 mb-6">
            Вашият акаунт беше създаден успешно. Ще бъдете пренасочени към страницата за вход...
          </p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 text-white bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 rounded-xl transition-all shadow-lg shadow-primary-500/25"
          >
            Влезте сега
          </Link>
        </div>
      </AuthLayout>
    );
  }

  // Registration form
  return (
    <AuthLayout>
      {/* Brand */}
      <AuthBrand subtitle="Приемете поканата" />

      <div className="text-center -mt-4">
        <p className="text-sm text-gray-400">
          Създайте своя акаунт за <strong className="text-gray-300">{invitation.email}</strong>
        </p>
        <p className="mt-1 text-xs text-gray-500">
          Роля: {invitation.role === 'admin' ? 'Администратор' : 'Служител'}
        </p>
      </div>

      {/* Error Alert */}
      {submitError && (
        <div className="auth-error px-4 py-3 rounded-xl">
          <p className="text-sm">{submitError}</p>
        </div>
      )}

      {/* Registration Form */}
      <form onSubmit={handleSubmit} className="mt-6 space-y-6">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Име *"
              type="text"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              error={formErrors.firstName}
              placeholder="Иван"
              autoComplete="given-name"
              autoFocus
            />
            <Input
              label="Фамилия *"
              type="text"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              error={formErrors.lastName}
              placeholder="Иванов"
              autoComplete="family-name"
            />
          </div>

          <Input
            label="Показвано име (опционално)"
            type="text"
            name="displayName"
            value={formData.displayName}
            onChange={handleChange}
            error={formErrors.displayName}
            placeholder="Иван Иванов"
            autoComplete="nickname"
          />

          <Input
            label="Парола *"
            type="password"
            name="password"
            value={formData.password}
            onChange={handleChange}
            error={formErrors.password}
            placeholder="Минимум 8 символа"
            autoComplete="new-password"
          />

          <Input
            label="Потвърдете паролата *"
            type="password"
            name="confirmPassword"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={formErrors.confirmPassword}
            placeholder="Повторете паролата"
            autoComplete="new-password"
          />
        </div>

        <Button
          type="submit"
          variant="primary"
          className="w-full py-3 rounded-xl font-semibold"
          disabled={acceptMutation.isPending}
        >
          {acceptMutation.isPending ? 'Създаване...' : 'Създай акаунт'}
        </Button>
      </form>

      <div className="pt-6 border-t border-white/[0.05] text-center text-sm text-gray-500">
        Вече имате акаунт?{' '}
        <Link to="/login" className="text-primary-400 hover:text-primary-300 transition-colors">
          Влезте тук
        </Link>
      </div>
    </AuthLayout>
  );
};

export default AcceptInvitationPage;
